import React, { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useIntl } from 'react-intl';

import SvgBuildingIcon from '@assets/svg/my-profile/icon-building.svg';
import SvgTrashIcon from '@assets/svg/my-profile/icon-trash.svg';
import { StyledImageUploader } from './image-uploader.styled';

const MEGABYTE_IN_BYTES = 1048576;

const resolveMessage = (isDragActive: boolean, isDragReject: boolean) => {
  if (isDragActive && isDragReject) return 'image_uploader_type_description';
  else if (isDragActive) return 'image_uploader_upload';
  else return 'image_uploader_heading';
};

type TPreview = { preview: string };
export type TFileWithPreview = (File & { preview: string }) | TPreview;

type TImageUploaderProps = {
  onChange?: (file: TFileWithPreview | undefined) => void; // Pass File to parent
  value?: TFileWithPreview | string; // A URL of a picture
};

/**
 * INP
 * @constructor
 */
const ImageUploader: React.FunctionComponent<TImageUploaderProps> = (
  props: TImageUploaderProps,
) => {
  const { value, onChange } = props;

  const { formatMessage } = useIntl();
  const [image, setImage] = useState<TFileWithPreview | string | undefined>();

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const imageFile = acceptedFiles[0];

    Object.assign(imageFile, {
      preview: URL.createObjectURL(imageFile),
    });

    setImage(imageFile as TFileWithPreview);
    onChange?.(imageFile as TFileWithPreview);
  }, []);

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpeg', '.png'] },
    maxFiles: 1,
    validator: (file: File) =>
      file.size > MEGABYTE_IN_BYTES
        ? {
            code: 'file-too-large',
            message: `File is larger than ${MEGABYTE_IN_BYTES} bytes`,
          }
        : null,
  });

  useEffect(() => {
    return () => {
      if (image && typeof image !== 'string') {
        URL.revokeObjectURL(image.preview);
      }
    };
  }, []);

  useEffect(() => {
    if (value) {
      setImage(value);
    }
  }, [value]);

  const handleRemoveImage = () => {
    if (image) {
      if (image && typeof image !== 'string') {
        URL.revokeObjectURL(image.preview);
      }
      setImage(undefined);
      onChange?.(undefined);
    }
  };

  return (
    <StyledImageUploader>
      {!image && (
        <button className="upload-content" {...getRootProps()} type="button">
          <input {...getInputProps()} />
          <img className="upload-content__image" src={SvgBuildingIcon} alt="content-area__image" />
          <p className="upload-content__heading">
            {formatMessage({
              id: resolveMessage(isDragActive, isDragReject),
            })}
          </p>
        </button>
      )}
      {image && (
        <div className="uploaded-image-content">
          <img src={typeof image === 'string' ? image : image.preview} />
          <button onClick={handleRemoveImage}>
            <img src={SvgTrashIcon} />
          </button>
        </div>
      )}
    </StyledImageUploader>
  );
};

export default ImageUploader;
